// Taşeron portalı — kenar çubuğu rail durumu (web-expert `uiPrefs.ts`'in rail kısmından kopya;
// app sınırı: import değil). Kullanıcı başına kalıcı; anahtar `principal.uuid`e göre ayrılır.
// `uuid` `useAuthStore`'dan SENKRON gelir (JWT'den çözülmüş) — bu yüzden yeniden yüklemede
// kenar çubuğu expanded→rail SIÇRAMAZ. Tüm depolama erişimi try/catch içindedir.

import { useAuthStore } from '@isgportal/shared';
import { useCallback, useState } from 'react';

export type TableDensity = 'sm' | 'md';

const DEFAULT_DENSITY: TableDensity = 'md';

function densityStorageKey(uuid: string): string {
  return `isgportal.ui.${uuid}.tableDensity`;
}

function isTableDensity(value: string | null): value is TableDensity {
  return value === 'sm' || value === 'md';
}

export function readTableDensity(uuid: string): TableDensity {
  try {
    const raw = window.localStorage.getItem(densityStorageKey(uuid));
    return isTableDensity(raw) ? raw : DEFAULT_DENSITY;
  } catch {
    return DEFAULT_DENSITY;
  }
}

export function writeTableDensity(uuid: string, density: TableDensity): void {
  try {
    window.localStorage.setItem(densityStorageKey(uuid), density);
  } catch {
    // Depolama kullanılamıyor (gizli mod/kota) — sessizce yok say.
  }
}

/** Tablo yoğunluğu — kullanıcı başına kalıcı (web-expert `useTableDensity`'den kopya). */
export function useTableDensity(): [TableDensity, (next: TableDensity) => void] {
  const uuid = useAuthStore((s) => s.principal?.uuid ?? null);
  const [density, setDensityState] = useState<TableDensity>(() =>
    uuid ? readTableDensity(uuid) : DEFAULT_DENSITY,
  );

  const setDensity = useCallback(
    (next: TableDensity) => {
      setDensityState(next);
      if (uuid) {
        writeTableDensity(uuid, next);
      }
    },
    [uuid],
  );

  return [density, setDensity];
}

function navRailStorageKey(uuid: string): string {
  return `isgportal.ui.${uuid}.navCollapsed`;
}

/**
 * Kenar çubuğu — kullanıcı başına kalıcı. `uuid` yoksa (oturum henüz hidrate olmadı)
 * bellek-içi varsayılana (`expanded`) düşer, kalıcılaştırmaz.
 */
export function useNavRailCollapsed(): [boolean, (next: boolean) => void] {
  const uuid = useAuthStore((s) => s.principal?.uuid ?? null);
  const [collapsed, setCollapsedState] = useState<boolean>(() => {
    if (!uuid) {
      return false;
    }
    try {
      return window.localStorage.getItem(navRailStorageKey(uuid)) === '1';
    } catch {
      return false;
    }
  });

  const setCollapsed = useCallback(
    (next: boolean) => {
      setCollapsedState(next);
      if (!uuid) {
        return;
      }
      try {
        window.localStorage.setItem(navRailStorageKey(uuid), next ? '1' : '0');
      } catch {
        // Depolama kullanılamıyor (gizli mod/kota) — sessizce yok say.
      }
    },
    [uuid],
  );

  return [collapsed, setCollapsed];
}
